import UserTargetsApi from "@/packages/api/Api/V1/UserTargetsApi";
import { jsonHeaders } from "@/packages/dashboard/csrf";
import { zRailsNumericString } from "@/packages/dashboard/api/z-rails-numeric";
import { withAppDomain } from "@/packages/dashboard/utils/url-utils";
import { z } from "zod";

const zOptionalRailsNumeric = z
  .union([zRailsNumericString, z.null(), z.undefined()])
  .transform((value) => (value === null || typeof value === "undefined" ? null : value));

const userTargetsSchema = z.object({
  calories_daily_target: zOptionalRailsNumeric,
  proteins_daily_target: zOptionalRailsNumeric,
  carbs_daily_target: zOptionalRailsNumeric,
  fats_daily_target: zOptionalRailsNumeric,
});

const userTargetsResponseSchema = z.object({
  user_targets: userTargetsSchema,
});

type ApiUserTargets = z.infer<typeof userTargetsSchema>;

export type UserTargets = {
  caloriesDailyTarget: number | null;
  proteinsDailyTarget: number | null;
  carbsDailyTarget: number | null;
  fatsDailyTarget: number | null;
};

const mapToClientTargets = (payload: ApiUserTargets): UserTargets => ({
  caloriesDailyTarget: payload.calories_daily_target,
  proteinsDailyTarget: payload.proteins_daily_target,
  carbsDailyTarget: payload.carbs_daily_target,
  fatsDailyTarget: payload.fats_daily_target,
});

const mapToServerPayload = (values: UserTargets): ApiUserTargets => ({
  calories_daily_target: values.caloriesDailyTarget,
  proteins_daily_target: values.proteinsDailyTarget,
  carbs_daily_target: values.carbsDailyTarget,
  fats_daily_target: values.fatsDailyTarget,
});

export const userTargetsQueryKey = ["user-targets"] as const;

export async function fetchUserTargets(): Promise<UserTargets> {
  const response = await fetch(withAppDomain(UserTargetsApi.show.path()), {
    headers: jsonHeaders(),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Failed to load user targets (${response.status}): ${errorBody}`);
  }

  const data = await response.json();
  const parsed = userTargetsResponseSchema.parse(data);
  return mapToClientTargets(parsed.user_targets);
}

export async function saveUserTargets(values: UserTargets): Promise<UserTargets> {
  const body = {
    user_targets: mapToServerPayload(values),
  };

  const response = await fetch(withAppDomain(UserTargetsApi.create.path()), {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Failed to save user targets (${response.status}): ${errorBody}`);
  }

  const data = await response.json();
  const parsed = userTargetsResponseSchema.parse(data);
  return mapToClientTargets(parsed.user_targets);
}
